import { useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Eye, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import {
  useInvitation,
  useCreateInvitation,
  useInvitationFamily,
} from '@/hooks/useInvitations';
import { useInvitationAutosave } from '@/hooks/useInvitationAutosave';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import {
  invitationSchema,
  invitationDefaults,
  InvitationFormValues,
} from '@/types/invitation-schema';
import type { ItineraryItem } from '@/types/invitation';
import AutosaveStatus from '@/components/invitation-editor/AutosaveStatus';
import EditorTabsList from '@/components/invitation-editor/EditorTabsList';
import CoupleTab from '@/components/invitation-editor/CoupleTab';
import CeremoniesTab from '@/components/invitation-editor/CeremoniesTab';
import LocationsTab from '@/components/invitation-editor/LocationsTab';
import FamilyTab from '@/components/invitation-editor/FamilyTab';
import GalleryTab from '@/components/invitation-editor/GalleryTab';
import DetailsTab from '@/components/invitation-editor/DetailsTab';
import GiftsTab from '@/components/invitation-editor/GiftsTab';
import MusicTab from '@/components/invitation-editor/MusicTab';
import SettingsTab from '@/components/invitation-editor/SettingsTab';
import PreviewTab from '@/components/invitation-editor/PreviewTab';

const hydrateInvitation = (existing: NonNullable<ReturnType<typeof useInvitation>['data']>) => ({
  ...invitationDefaults,
  ...existing,
  wedding_date: existing.wedding_date.split('T')[0],
  itinerary: existing.itinerary || [],
  sections: (existing.sections as InvitationFormValues['sections']) ?? {},
  religious_ceremony_name: existing.religious_ceremony_name || '',
  religious_ceremony_address: existing.religious_ceremony_address || '',
  religious_ceremony_time: existing.religious_ceremony_time || '',
  religious_ceremony_map_url: existing.religious_ceremony_map_url || '',
  civil_ceremony_name: existing.civil_ceremony_name || '',
  civil_ceremony_address: existing.civil_ceremony_address || '',
  civil_ceremony_time: existing.civil_ceremony_time || '',
  civil_ceremony_map_url: existing.civil_ceremony_map_url || '',
  party_name: existing.party_name || '',
  party_address: existing.party_address || '',
  party_time: existing.party_time || '',
  party_map_url: existing.party_map_url || '',
  dresscode: existing.dresscode || '',
  background_music_url: existing.background_music_url || '',
  gifts_message: existing.gifts_message || '',
  bank_account_holder: existing.bank_account_holder || '',
  bank_account_number: existing.bank_account_number || '',
  bank_name: existing.bank_name || '',
  gift_registry_url: existing.gift_registry_url || '',
  qr_image_url: existing.qr_image_url || '',
});

const InvitationEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const isNew = !id || id === 'new';
  const invitationId = isNew ? undefined : id;

  const { data: existingInvitation, isLoading: loadingInvitation } = useInvitation(id || '');
  const createInvitation = useCreateInvitation();
  const { data: familyMembers } = useInvitationFamily(invitationId || '');

  const form = useForm<InvitationFormValues>({
    resolver: zodResolver(invitationSchema),
    defaultValues: invitationDefaults,
    mode: 'onBlur',
  });

  const autosave = useInvitationAutosave(invitationId, form, !isNew);
  const hasHydrated = useRef(false);

  useEffect(() => {
    if (!authLoading && !user) navigate('/auth');
  }, [user, authLoading, navigate]);

  // Hydrate the form once on first successful fetch. A later refetch (e.g.
  // window-focus, post-publish invalidation) must NOT clobber in-progress edits.
  useEffect(() => {
    if (existingInvitation && !hasHydrated.current) {
      form.reset(hydrateInvitation(existingInvitation));
      hasHydrated.current = true;
    }
  }, [existingInvitation, form]);

  const handleCreate = async () => {
    const valid = await form.trigger(['bride_name', 'groom_name', 'wedding_date']);
    if (!valid) {
      toast.error('Por favor completa los campos obligatorios');
      return;
    }
    try {
      const values = form.getValues() as InvitationFormValues;
      const result = await createInvitation.mutateAsync({
        ...values,
        itinerary: values.itinerary as ItineraryItem[],
      });
      navigate(`/invitations/${result.id}/edit`);
    } catch {
      // toast handled by mutation
    }
  };

  const previewFamily = useMemo(
    () =>
      familyMembers?.map((m) => ({
        id: m.id,
        name: m.name,
        role: m.role,
        display_order: m.display_order ?? undefined,
      })) || [],
    [familyMembers],
  );

  const headerNames = form.watch(['bride_name', 'groom_name']);

  if (authLoading || (!isNew && loadingInvitation)) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <FormProvider {...form}>
        <div className="container mx-auto py-8 px-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-display font-bold">
                  {isNew ? 'Nueva Invitación' : 'Editar Invitación'}
                </h1>
                <p className="text-muted-foreground text-sm">
                  {headerNames[0] && headerNames[1]
                    ? `${headerNames[0]} & ${headerNames[1]}`
                    : 'Completa los datos de tu boda'}
                </p>
              </div>
            </div>
            <div className="flex gap-3 items-center">
              {!isNew && (
                <AutosaveStatus
                  status={autosave.status}
                  lastSavedAt={autosave.lastSavedAt}
                  onRetry={autosave.retryNow}
                />
              )}
              {existingInvitation?.is_published && (
                <Button variant="outline" asChild>
                  <a href={`/i/${existingInvitation.unique_slug}`} target="_blank" rel="noopener">
                    <Eye className="w-4 h-4 mr-2" />
                    Ver Invitación
                  </a>
                </Button>
              )}
              {isNew && (
                <Button onClick={handleCreate} disabled={createInvitation.isPending}>
                  <Save className="w-4 h-4 mr-2" />
                  Guardar
                </Button>
              )}
            </div>
          </div>

          <Tabs defaultValue="couple" className="space-y-6">
            <EditorTabsList />
            <TabsContent value="couple"><CoupleTab /></TabsContent>
            <TabsContent value="ceremonies"><CeremoniesTab /></TabsContent>
            <TabsContent value="locations"><LocationsTab /></TabsContent>
            <TabsContent value="family"><FamilyTab invitationId={invitationId} /></TabsContent>
            <TabsContent value="gifts"><GiftsTab invitationId={invitationId} /></TabsContent>
            <TabsContent value="gallery"><GalleryTab invitationId={invitationId} /></TabsContent>
            <TabsContent value="details"><DetailsTab invitationId={invitationId} /></TabsContent>
            <TabsContent value="music"><MusicTab invitationId={invitationId} /></TabsContent>
            <TabsContent value="settings">
              <SettingsTab
                invitationId={invitationId}
                publicSlug={existingInvitation?.unique_slug || null}
                isPaid={!!existingInvitation?.is_paid}
              />
            </TabsContent>
            <TabsContent value="preview">
              <PreviewTab
                isPublished={!!existingInvitation?.is_published}
                publicSlug={existingInvitation?.unique_slug || null}
                familyMembers={previewFamily}
              />
            </TabsContent>
          </Tabs>
        </div>
      </FormProvider>
    </Layout>
  );
};

export default InvitationEditor;
